// ABOUTME: Modal for capturing photos for a return or inbound line via PC webcam or Scanner barcode
// ABOUTME: Features mode toggle, webcam capture, barcode display for scanner, and polling for scanner uploads

import { useCallback, useEffect, useRef, useState } from "react";
import { Box, ButtonBase, CircularProgress, useTheme } from "@mui/material";
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import Barcode from 'react-barcode';
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import SHModal from "../../theme/Modal/SHModal";
import { tokens } from "../../../theme";
import Text from "../../theme/text/Text";
import Subheading from "../../theme/text/Subheading";
import Shbutton from "../../theme/buttons/Shbutton";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import ButtonGroup from "../../theme/buttons/ButtonGroup";

// Domain-agnostic: the caller owns the photo list and the upload/delete calls, so the same
// modal serves return lines and inbound lines.
const MLinePhotoCapture = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const {
        open,
        onClose,
        barcodeValue,
        photos = [],
        refetchPhotos,
        onUpload,
        onDelete,
        isUploading = false,
        isDeleting = false,
        isFetchingPhotos = false,
        initialMode = 'pc',
        onPhotosChanged,
        onPhotoClick,
        showScanner = true,
    } = props;

    const [mode, setMode] = useState(showScanner ? initialMode : 'pc');
    const [cameraError, setCameraError] = useState(null);
    const [sessionPhotos, setSessionPhotos] = useState([]);
    const [pollingActive, setPollingActive] = useState(false);
    const [lastPhotoCount, setLastPhotoCount] = useState(0);

    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const timerRef = useRef(null);
    const timeoutRef = useRef(null);

    useEffect(() => {
        if (open) {
            setMode(showScanner ? initialMode : 'pc');
            setSessionPhotos([]);
            setCameraError(null);
            setLastPhotoCount(photos.length);
            if (showScanner && initialMode === 'scanner') {
                setPollingActive(true);
            }
        }
    }, [open, initialMode]);

    useEffect(() => {
        if (mode === 'pc' && open) {
            startCamera();
        } else {
            stopCamera();
        }

        if (mode === 'scanner' && open) {
            setPollingActive(true);
        } else {
            setPollingActive(false);
        }

        return () => {
            stopCamera();
        };
    }, [mode, open]);

    useEffect(() => {
        if (mode !== 'scanner' || !pollingActive || !open) {
            clearInterval(timerRef.current);
            clearTimeout(timeoutRef.current);
            return;
        }

        timeoutRef.current = setTimeout(() => {
            setPollingActive(false);
        }, 60000);

        timerRef.current = setInterval(() => {
            refetchPhotos();
        }, 3000);

        return () => {
            clearInterval(timerRef.current);
            clearTimeout(timeoutRef.current);
        };
    }, [mode, pollingActive, open, refetchPhotos]);

    useEffect(() => {
        const currentPhotoCount = photos.length;
        if (currentPhotoCount > lastPhotoCount && mode === 'scanner') {
            setLastPhotoCount(currentPhotoCount);
            clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
                setPollingActive(false);
            }, 60000);
            toast.success(t("photo_uploaded"));
        }
    }, [photos.length, lastPhotoCount, mode, t]);

    const startCamera = async () => {
        try {
            setCameraError(null);
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    width: { ideal: 640 },
                    height: { ideal: 480 }
                }
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
        } catch (err) {
            console.error("Camera error:", err);
            setCameraError(t("camera_access_denied"));
        }
    };

    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    };

    const capturePhoto = useCallback(async (keepOpen = false) => {
        if (!canvasRef.current || !videoRef.current) return;

        const canvas = canvasRef.current;
        const video = videoRef.current;

        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 480;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(async (blob) => {
            if (!blob) return;

            const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
            const localUrl = URL.createObjectURL(blob);
            const tempId = `temp-${Date.now()}`;

            // Add temporary preview with local blob URL
            setSessionPhotos(prev => [...prev, { id: tempId, url: localUrl, isTemp: true }]);

            try {
                const result = await onUpload(file);

                // Extract photo from response (backend returns {success, photo})
                const uploadedPhoto = result.photo || result;

                // Replace temporary photo with actual uploaded photo
                setSessionPhotos(prev => prev.map(p =>
                    p.id === tempId ? uploadedPhoto : p
                ));

                // Clean up blob URL
                URL.revokeObjectURL(localUrl);

                toast.success(t("photo_uploaded"));

                if (onPhotosChanged) {
                    onPhotosChanged();
                }

                if (!keepOpen) {
                    handleClose();
                }
            } catch (err) {
                // Remove temporary photo on error
                setSessionPhotos(prev => prev.filter(p => p.id !== tempId));
                URL.revokeObjectURL(localUrl);
                toast.error(t("error"));
            }
        }, 'image/jpeg', 0.85);
    }, [onUpload, t, onPhotosChanged]);

    const handleDeleteSessionPhoto = async (photoId) => {
        try {
            await onDelete(photoId);

            setSessionPhotos(prev => prev.filter(p => p.id !== photoId));
            toast.success(t("photo_deleted"));

            if (onPhotosChanged) {
                onPhotosChanged();
            }
        } catch (err) {
            toast.error(t("error"));
        }
    };

    const handleDeleteScannerPhoto = async (photoId) => {
        try {
            await onDelete(photoId);

            toast.success(t("photo_deleted"));
            refetchPhotos();

            if (onPhotosChanged) {
                onPhotosChanged();
            }
        } catch (err) {
            toast.error(t("error"));
        }
    };

    const handleClose = () => {
        stopCamera();
        setPollingActive(false);
        clearInterval(timerRef.current);
        clearTimeout(timeoutRef.current);
        setSessionPhotos([]);
        setCameraError(null);
        onClose();
    };

    const handleRefresh = () => {
        refetchPhotos();
        setPollingActive(true);
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            setPollingActive(false);
        }, 60000);
    };

    const scannerPhotos = photos;

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    return (
        <SHModal
            open={open}
            onClose={handleClose}
            sx={{
                body: {
                    minWidth: 600,
                    maxWidth: 700,
                }
            }}
        >
            {showScanner &&
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    py:3,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                        borderRadius: 5,
                        p: 0.5,
                    }}
                >
                    <ButtonBase
                        onClick={() => setMode('pc')}
                        sx={{
                            px: 3,
                            py: 1,
                            borderRadius: 4,
                            fontWeight: 600,
                            fontSize: 14,
                            fontFamily: "Roboto, sans-serif",
                            background: mode === 'pc'
                                ? (theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400])
                                : "transparent",
                            color: mode === 'pc'
                                ? theme.palette.mode === "dark" ? colors.txt['primaryDark'] : colors.txt["light"]
                                : colors.txt["primary"],
                        }}
                    >
                        {t("pc_mode")}
                    </ButtonBase>
                    <ButtonBase
                        onClick={() => setMode('scanner')}
                        sx={{
                            px: 3,
                            py: 1,
                            borderRadius: 4,
                            fontWeight: 600,
                            fontSize: 14,
                            fontFamily: "Roboto, sans-serif",
                            background: mode === 'scanner'
                                ? (theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400])
                                : "transparent",
                            color: mode === 'scanner'
                                ? theme.palette.mode === "dark" ? colors.txt['primaryDark'] : colors.txt["light"]
                                : colors.txt["primary"],
                        }}
                    >
                        {t("scanner_mode")}
                    </ButtonBase>
                </Box>
            </Box>
            }

            {mode === 'pc' && (
                <Box
                    sx={{
                        pt:3,
                        background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                         borderRadius:4,
                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            aspectRatio: "4/3",
                            background: colors.grey[900],
                            borderRadius: 4,
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 2,
                            px:3,
                        }}
                    >
                        {cameraError ? (
                            <Text variant="red">{cameraError}</Text>
                        ) : (
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    borderRadius:"16px",
                                }}
                            />
                        )}
                    </Box>
                    <canvas ref={canvasRef} style={{ display: 'none' }} />

                    {sessionPhotos.length > 0 && (
                        <Box
                            sx={{
                                mx:3,
                                mt:1.5,
                            }}
                        >
                            <Subheading sx={{ mb: 1 }}>{t("photos")}</Subheading>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                }}
                            >
                                {sessionPhotos.map((photo, index) => (
                                    <Box
                                        key={photo.id}
                                        sx={{
                                            position: "relative",
                                            width: 80,
                                            height: 80,
                                            borderRadius: 2,
                                            overflow: "hidden",
                                            border: `1px solid ${colors.grey[200]}`,
                                        }}
                                    >
                                        <ButtonBase
                                            onClick={() => onPhotoClick && onPhotoClick(index)}
                                            sx={{
                                                width: "100%",
                                                height: "100%",
                                                padding: 0,
                                                display: "block",
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={photo.url || photo.imageUrl}
                                                alt=""
                                                sx={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                }}
                                            />
                                        </ButtonBase>
                                        <ButtonBase
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteSessionPhoto(photo.id);
                                            }}
                                            disabled={isDeleting}
                                            sx={{
                                                position: "absolute",
                                                top: 2,
                                                right: 2,
                                                width: 20,
                                                height: 20,
                                                borderRadius: "50%",
                                                background: colors.red[400],
                                                color: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                            }}
                                        >
                                            <CloseIcon sx={{ fontSize: 14 }} />
                                        </ButtonBase>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}
                    <Box
                        component="footer"
                        sx={{
                            
                            display:"block",
                            width:1,
                            paddingBottom: isStandAlone ? "32px" : "16px",
                            display:"flex",
                            alignItems:"center",
                            paddingRight:3,
                            zIndex:1058,
                            position:"relative",
                            paddingTop:2,
                            background:`${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                            borderBottomLeftRadius:24,
                            borderBottomRightRadius:24,
                            justifyContent:"space-between",
                            "@media screen and (max-width: 47.9375em)" : {
                                paddingRight:0,
                            }
                        }}
                    >
                        
                        <Shbutton isCancel={true} className={"always-visible"} onClick={handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon/>}>{t("cancel")}</Shbutton>
                        <ButtonGroup version={2}>
                        
                            <Shbutton
                                onClick={() => capturePhoto(false)}
                                disabled={isUploading || !!cameraError}
                                isLoading={isUploading}
                                color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                variant="contained"
                            >
                                {t("take_photo")}
                            </Shbutton>
                            <Shbutton
                                onClick={() => capturePhoto(true)}
                                disabled={isUploading || !!cameraError}
                                color="grey"
                                variant="contained"
                            >
                                {t("take_and_add_another")}
                            </Shbutton>

                        </ButtonGroup>
                    </Box>

                   

                </Box>
            )}

            {showScanner && mode === 'scanner' && (
                <Box
                    sx={{
                        background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                        borderRadius:4,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            px: 2,
                            mb: 3,
                        }}
                    >
                        <Box
                            sx={{
                                background: "white",
                                p: 2,
                                borderRadius: 2,
                            }}
                        >
                            <Barcode
                                value={barcodeValue}
                                format="CODE128"
                                lineColor={colors.txt['primary']}
                                width={2}
                                height={80}
                                displayValue={true}
                                fontSize={14}
                                margin={10}
                            />
                        </Box>
                        <Text light center>{t("scan_barcode_with_scanner")}</Text>

                        <Box
                            sx={{
                                mt: 2,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            {pollingActive && (
                                <>
                                    <CircularProgress size={16} />
                                    <Text light>{t("waiting_for_photos")}</Text>
                                </>
                            )}
                        </Box>
                    </Box>


                    <Box
                        sx={{
                            display: "flex",
                            flexDirection:"column",
                            gap: 1.5,
                            mb: 3,
                            mx:3,
                            borderRadius:4,
                            px:2,
                            py:2,
                            background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                        }}
                    >
                        <Box
                            sx={{
                                display:"flex",
                                justifyContent:"space-between",
                                alignItems:"center",
                            }}
                        >
                            <Subheading>{t("photos")}</Subheading>
                            <ButtonBase
                                onClick={handleRefresh}
                                disabled={isFetchingPhotos}
                                sx={{
                                    height: 32,
                                    px: 2,
                                    borderRadius: 4,
                                    background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                {isFetchingPhotos ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <RefreshIcon sx={{ fontSize: 18 }} />
                                )}
                                <Text>{t("refresh")}</Text>
                            </ButtonBase>
                        </Box>
                        <Box
                            sx={{
                                display:'flex',
                                flexWrap:"wrap",
                                width:1,
                                gap:1.5,
                            }}
                        >
                            {scannerPhotos.length === 0 ? (
                                <Box
                                    sx={{
                                        width: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <Text light>{t("no_photos_yet")}</Text>
                                </Box>
                            ) : (
                                scannerPhotos.map((photo, index) => (
                                    <Box
                                        key={photo.id}
                                        sx={{
                                            position: "relative",
                                            width: 96,
                                            height: 96,
                                            borderRadius: 3,
                                            overflow: "hidden",
                                            border: `1px solid ${colors.grey[200]}`,
                                        }}
                                    >
                                        <ButtonBase
                                            onClick={() => onPhotoClick && onPhotoClick(index)}
                                            sx={{
                                                width: "100%",
                                                height: "100%",
                                                padding: 0,
                                                display: "block",
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={photo.url}
                                                alt=""
                                                sx={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                }}
                                            />
                                        </ButtonBase>
                                        <ButtonBase
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteScannerPhoto(photo.id);
                                            }}
                                            disabled={isDeleting}
                                            sx={{
                                                position: "absolute",
                                                top: 4,
                                                right: 4,
                                                width: 24,
                                                height: 24,
                                                borderRadius: "50%",
                                                background: colors.red[400],
                                                color: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                            }}
                                        >
                                            <CloseIcon sx={{ fontSize: 16 }} />
                                        </ButtonBase>
                                    </Box>
                                ))
                            )}
                        </Box>
                    </Box>

                     <Box
                        component="footer"
                        sx={{
                            
                            display:"block",
                            width:1,
                            paddingBottom: isStandAlone ? "32px" : "16px",
                            display:"flex",
                            alignItems:"center",
                            paddingRight:3,
                            zIndex:1058,
                            position:"relative",
                            background:`${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                            borderBottomLeftRadius:24,
                            borderBottomRightRadius:24,
                            justifyContent:"space-between",
                            "@media screen and (max-width: 47.9375em)" : {
                                paddingRight:0,
                            }
                        }}
                    >
                        
                        <Shbutton isCancel={true} className={"always-visible"} onClick={handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon/>}>{t("cancel")}</Shbutton>
                        <ButtonGroup version={2}>
                        
                            <Shbutton
                                onClick={handleClose}
                                color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                variant="contained"
                            >
                                {t("done")}
                            </Shbutton>

                        </ButtonGroup>
                    </Box>

                </Box>
            )}
        </SHModal>
    );
};

export default MLinePhotoCapture;
